import type { ErrorInfo, FC, PropsWithChildren, ReactNode } from "react";
import { Component, useEffect, useState } from "react";

import isNil from "lodash/isNil";

import { LogoutReason } from "@/constants/Login";
import useAuth from "@/hooks/useAuth";
import { captureException } from "@/services/AppMonitor";
import {
  useAppStartupDispatch,
  useAuth0RenewFailureSelector,
} from "@/store/reducers/appStartup";
import { handleAuthError } from "@/utils/auth.util";

type ErrorHandler = (errorHandlerInputs: ErrorHandlerInputs) => void;

interface ErrorHandlerInputs {
  error: Error;
  errorInfo: ErrorInfo;
}

interface ErrorBoundaryProps extends PropsWithChildren {
  fallback?: ReactNode;
  hasErrorOverride?: boolean;
  onError?: ErrorHandler;
}

interface ErrorBoundaryState {
  error?: Error;
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static readonly defaultProps = {
    fallback: undefined,
    onError: (errorHandlerInputs: ErrorHandlerInputs) => {
      try {
        // log fatal error to analytics services (Datadog)
        captureException(errorHandlerInputs.error, {
          context: { errorInfo: errorHandlerInputs.errorInfo },
        });
      } catch {
        // Could not log fatal error to analytics services
      }
    },
  };

  static readonly getDerivedStateFromError = (
    error: Error,
  ): ErrorBoundaryState => ({
    error,
    hasError: true,
  });

  // eslint-disable-next-line max-params
  override componentDidCatch = (error: Error, errorInfo: ErrorInfo): void => {
    const { onError } = this.props;
    onError?.({ error, errorInfo });
  };

  // eslint-disable-next-line sonarjs/function-return-type -- currently reported in SonarQube as unresolved
  override render = (): ReactNode => {
    const { children, fallback, hasErrorOverride } = this.props;
    const { hasError } = this.state;
    if ((hasError || hasErrorOverride) && fallback) return fallback;
    return children;
  };

  override state: ErrorBoundaryState = {
    error: undefined,
    hasError: false,
  };
}

const ErrorBoundaryProvider: FC<ErrorBoundaryProps> = ({
  children,
  fallback,
  hasErrorOverride,
  onError,
}) => {
  const { setAuth0RenewFailure } = useAppStartupDispatch();
  const { logout } = useAuth();
  const auth0RenewFailure = useAuth0RenewFailureSelector();
  const [shouldForceHasError, setShouldForceHasError] = useState<boolean>(
    Boolean(hasErrorOverride),
  );

  useEffect(() => {
    if (hasErrorOverride) {
      setShouldForceHasError(true);
      return;
    }

    if (isNil(auth0RenewFailure)) return;

    const { error } = auth0RenewFailure;
    const extra = auth0RenewFailure.extra ?? {};
    handleAuthError({
      context: extra,
      error,
      scenario: `RENEW_FAILED captured in ErrorBoundaryProvider, attempting logout. Original scenario: ${extra.scenario}`,
    });
    // clear the renewal failure flag and attempt logout/cleanup
    setAuth0RenewFailure(undefined);
    void logout(LogoutReason.AuthError);

    // if logout succeeds: credentials cleared, navigate to Login
    // if logout fails: hasRevokeAuthTokenFailed or other flags set, restart required
  }, [auth0RenewFailure, hasErrorOverride, logout, setAuth0RenewFailure]);

  return (
    <ErrorBoundary
      fallback={fallback}
      hasErrorOverride={shouldForceHasError}
      onError={onError}
    >
      {children}
    </ErrorBoundary>
  );
};

export { ErrorBoundary };

export default ErrorBoundaryProvider;
